import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography } from '@material-ui/core'
import ProjectedAverageCostOfLaborSummaryReport from './ProjectedAverageCostOfLaborSummaryReport'
import DataFilters from '../../DataFilters'
import { range, find, get } from 'lodash'
import { makeStyles } from '@material-ui/styles'
import Select from 'react-select'
import axios from 'axios'
import { round } from '../../../../../helpers'


// Styles
const useStyles = makeStyles(theme => ({
	forecastSelectionArea: {
		width: 200,
		display: 'block',
		marginBottom: 14,
		margin: '0 auto',
		paddingBottom: 13,
	},
}))


export default function AverageCostOfLaborSummary(props) {

	const classes = useStyles()
	const [workforceData, setWorkforceData] = useState({})
	const [applyFilter, setApplyFilter] = useState(false)

	const currencyRate = props.selectedCurrency !== null ? props.selectedCurrency.dollar_rate : 1

	useEffect(() => {
		if (props.selectedForecast) {
			axios.post(`/api/workforce_forecast/filters/${props.selectedForecast.value}`, props.reportFilters)
				.then((result) => {
					const categoryToDataMap = {}
					result.data.data.forEach(d => {
						if (!categoryToDataMap[d.job_grade]) {
							categoryToDataMap[d.job_grade] = { hierarchy: d.hierarchy, employeesCount: d.employees_count, estimatedSalary: d.estimated_salary_usd }
						} else {
							categoryToDataMap[d.job_grade] = { hierarchy: d.hierarchy, employeesCount: categoryToDataMap[d.job_grade].employeesCount + d.employees_count, estimatedSalary: categoryToDataMap[d.job_grade].estimatedSalary + d.estimated_salary_usd }
						}
					})
					setWorkforceData(categoryToDataMap)
				})
				.catch((error) => {
					console.log(error)
					setWorkforceData([])
				})
		}
	}, [props.selectedForecast, applyFilter])

	const groups = ['E', 'M', 'P', 'S']

	const groupsNames = {
		'E': 'Executive',
		'M': 'Management',
		'P': 'Professional',
		'S': 'Support',
	}

	const onApply = (filters) => {
		props.onApply(filters, 'ProjectedAverageCostOfLaborSummary')
	}

	const levelsData = []
	const groupSummary = {
		current: { totalN: 0, totalSalary: 0, averageTotalSalary: 0, totalPercent: 0, NofAverages: 0},
		growth: { totalN: 0, totalSalary: 0, averageTotalSalary: 0, totalPercent: 0, NofAverages: 0},
		future: { totalN: 0, totalSalary: 0, averageTotalSalary: 0, totalPercent: 0, NofAverages: 0},
	}

	range(14).forEach(level => {
		const levelData = props.masterData.jobGrades.filter(hd => hd.hierarchy === level)
		const groupData = {
			'current': { totalN: 0, totalSalary: 0, averageTotalSalary: 0, percent: 0},
			'growth': { totalN: 0, totalSalary: 0, averageTotalSalary: 0, percent: 0},
			'future': { totalN: 0, totalSalary: 0, averageTotalSalary: 0, percent: 0},
		}

		groups.forEach(g => {
			groupData[g] = {
				level: '',
				title: '',
			}
		})

		levelData.forEach(ld => {
			const groupKey = ld.job_grade.slice(0, 1)
			const dataInReport = find(props.reportData, { 'compkit_job_grade': ld.job_grade })
			const dataInWorkforce = get(workforceData, ld.job_grade, null)

			groupData[groupKey].level = ld.job_grade
			groupData[groupKey].title = ld.job_title

			groupData.current.totalN += get(dataInReport, 'total', 0)
			groupData.current.totalSalary += round((get(dataInReport, 'avg_annual_base_salary', 0) * get(dataInReport, 'total', 0)) / currencyRate)
			groupData.current.averageTotalSalary = round((groupData.current.totalSalary / groupData.current.totalN) / currencyRate)

			groupData.growth.totalN += get(dataInWorkforce, 'employeesCount', 0)
			groupData.growth.totalSalary += round(get(dataInWorkforce, 'estimatedSalary', 0) / currencyRate)
			groupData.growth.averageTotalSalary = round((groupData.growth.totalSalary / groupData.growth.totalN) / currencyRate)

			groupData.future.totalN = groupData.current.totalN + groupData.growth.totalN
			groupData.future.totalSalary = round(groupData.current.totalSalary + groupData.growth.totalSalary)
			groupData.future.averageTotalSalary = round(groupData.future.totalSalary / groupData.future.totalN)
		})

		groupSummary.current.totalN += groupData.current.totalN
		groupSummary.current.totalSalary += groupData.current.totalSalary
		groupSummary.current.averageTotalSalary += groupData.current.averageTotalSalary
		if(groupData.current.averageTotalSalary)
		{
			groupSummary.current.NofAverages += 1
		}

		groupSummary.growth.totalN += groupData.growth.totalN
		groupSummary.growth.totalSalary += groupData.growth.totalSalary
		groupSummary.growth.averageTotalSalary += groupData.growth.averageTotalSalary
		if(groupData.growth.averageTotalSalary)
		{
			groupSummary.growth.NofAverages += 1
		}

		groupSummary.future.totalN += groupData.future.totalN
		groupSummary.future.totalSalary += groupData.future.totalSalary
		groupSummary.future.averageTotalSalary += groupData.future.averageTotalSalary
		if(groupData.future.averageTotalSalary)
		{
			groupSummary.future.NofAverages += 1
		}

		levelsData.push(groupData)
	})

	levelsData.forEach(level => {
		level.current.percent = round((level.current.totalN / groupSummary.current.totalN) * 100)
		level.growth.percent = round((level.growth.totalN / groupSummary.growth.totalN) * 100)
		level.future.percent = round((level.future.totalN / groupSummary.future.totalN) * 100)
		groupSummary.current.totalPercent = round(groupSummary.current.totalPercent + level.current.percent)
		groupSummary.growth.totalPercent = round(groupSummary.growth.totalPercent + level.growth.percent)
		groupSummary.future.totalPercent = round(groupSummary.future.totalPercent + level.future.percent)
	})

	groupSummary.current.totalPercent = round(groupSummary.current.totalPercent, 0)
	groupSummary.growth.totalPercent = round(groupSummary.growth.totalPercent, 0)
	groupSummary.future.totalPercent = round(groupSummary.future.totalPercent, 0)


	groupSummary.current.averageTotalSalary = groupSummary.current.averageTotalSalary? groupSummary.current.averageTotalSalary/groupSummary.current.NofAverages : 0
	groupSummary.growth.averageTotalSalary = groupSummary.growth.averageTotalSalary? groupSummary.growth.averageTotalSalary/groupSummary.growth.NofAverages : 0
	groupSummary.future.averageTotalSalary = groupSummary.future.averageTotalSalary? groupSummary.future.averageTotalSalary/groupSummary.future.NofAverages : 0

	/**
	 * Below is the Display logic for Rows
	 */
	levelsData.forEach(level => {
		level.current.percent = level.current.percent.toFixed(1)
		level.growth.percent = level.growth.percent.toFixed(1)
		level.future.percent = level.future.percent.toFixed(1)
	})

	return (
		<>
			<DataFilters
				fetchedFilterData={props.fetchedFilterData}
				filtersData={props.filtersData}
				onDataFilterSelect={props.onDataFilterSelect}
				componentName={'ProjectedAverageCostOfLaborSummary'}
				reportFilters={props.reportFilters}
				onApply={onApply}
				resetFilter={props.resetFilter}
				reportFiltersType={props.reportFiltersType}
				reportDataChanged={props.reportDataChanged}
				setApplyFilters={setApplyFilter}
				applyFilters={applyFilter}
			/>
			<div className={classes.forecastSelectionArea}>
				<Typography display='inline'>Select Hiring Forecast</Typography>
				<Select
					value={props.selectedForecast}
					onChange={(selectedForecastOption) => props.forecastSelectionChanged('organizationProjectedSummary', selectedForecastOption)}
					options={props.forecastOptions}
				/>
			</div>
			<ProjectedAverageCostOfLaborSummaryReport
				groups={groups}
				groupsNames={groupsNames}
				levelsData={levelsData}
				groupSummary={groupSummary}
				currencyCode={props.selectedCurrency !== null ? props.selectedCurrency.short_code : 'USD'}
			/>
		</>
	)
}

AverageCostOfLaborSummary.propTypes = {
	masterData: PropTypes.object.isRequired,
	onDataFilterSelect: PropTypes.func.isRequired,
	reportFilters: PropTypes.object.isRequired,
	reportData: PropTypes.array.isRequired,
	onApply: PropTypes.func.isRequired,
}
